import { useMemo } from "react";
import { plural } from "@ptsd/uikit";

import type { RouterOutputs } from "~/utils/api";
import { isNonEmptyString } from "~/utils/is-non-empty-string";

export type ScalesData = Exclude<
  NonNullable<RouterOutputs["scores"]["bySphere"]>["scales"],
  never[]
>;
export type SlicesScalesData = NonNullable<
  RouterOutputs["slices"]["getResults"]
>["scales"];

export type CriticalScaleData = NonNullable<
  RouterOutputs["scores"]["criticalScales"]
>;

type CompatibleScale =
  | ScalesData[number]
  | SlicesScalesData[number]
  | CriticalScaleData[number];

const levelToStatusMap = {
  neutral: "neutral",
  low: "success",
  belowMedium: "success",
  medium: "warning",
  aboveMedium: "danger",
  high: "danger",
} as const;

//TODO: move to backend
export const useConstructScales = <T extends CompatibleScale[]>(data: T) => {
  const result = useMemo(() => {
    if (!data) return [];
    return data.map(
      (
        { dynamics, interpretation, score, level, label, shortInfo, details },
        index,
      ) => {
        const hasScore = !isNaN(score);
        const state = levelToStatusMap[level];

        return {
          id: `scale-${index}`,
          score: isNaN(score) ? "X" : score,
          title: label,
          state,
          stateText: isNaN(score) ? "Шкала не надавалась" : shortInfo,
          accordion: [
            isNonEmptyString(details)
              ? {
                  id: "details",
                  title: "Що визначає ця шкала?",
                  state,
                  content: { mdText: details },
                }
              : undefined,
            {
              id: "interpretation",
              state,
              title: isNaN(score)
                ? "Чому відсутній результат?"
                : "Що означає мій результат?",
              content: {
                title: hasScore
                  ? `${score} ${plural(score, "Бал", "Бали", "Балів")}`
                  : undefined,
                description: hasScore
                  ? "Шкала оцінюється від 0 до 100 балів: що вищий показник, то більш вираженими є відповідні прояви."
                  : undefined,
                mdText: interpretation,
              },
            },
          ].filter(Boolean) as {
            title: string;
            state: "neutral" | "success" | "danger" | "warning";
            content: { title: string; description: string; mdText: string };
            id: string;
          }[],

          chart: {
            config: {
              percentage: { label: label, color: "#EC1E9D" },
            },
            data: dynamics,
          },
        };
      },
    );
  }, [data]);
  return result;
};
